import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import Input from '../../../reusable/Input/Input';
import AuthSlider from '../../../reusable/AuthSlider/AuthSlider';
import TwoFactorDialog from './TwoFactorDialog';
import { useLoginMutation } from '../../../store/api/authApi';
import { authErrorMessage } from '../../../store/api/authHelpers';
import { googleAuthUrl, rememberForGoogle } from '../../../utilities/googleAuth';
import { t } from '../../../i18n/i18n';
import './SignIn.scss';

/**
 * The legal pages live's footer links (`ReusableComponents/Footer` on main),
 * in its order and with its labels — at the addresses those pages now answer
 * on: live's privacy link (`/privacy-notice/`) is a 404 and its terms link
 * redirects to `/terms-and-conditions`, so both point straight there (N72).
 */
const LEGAL_LINKS = [
  { label: 'Privacy Policy', href: 'https://alphacapitalgroup.uk/privacy-policy' },
  { label: 'Terms', href: 'https://alphacapitalgroup.uk/terms-and-conditions' },
  { label: 'Disclaimer', href: 'https://alphacapitalgroup.uk/disclaimer' },
  { label: 'Return Policy', href: 'https://alphacapitalgroup.uk/return-policy' },
];

const signInSchema = Yup.object({
  email: Yup.string()
    .trim()
    .email('Please enter a valid email address')
    .required('Email is required'),
  password: Yup.string().required('Password is required'),
  rememberMe: Yup.boolean(),
});

export default function SignIn() {
  const navigate = useNavigate();
  const location = useLocation();
  const [login, { isLoading, error: serverError }] = useLoginMutation();

  /**
   * The `temp_token` from a `requires_2fa` sign-in, held only while the dialog
   * is open. It is single-use and short-lived, so it never goes near the store
   * or the URL — cancelling the dialog drops it and the password has to be
   * entered again.
   */
  const [tempToken, setTempToken] = useState(null);

  /** Wherever the user was heading before PrivateRoute sent them here. */
  const goOn = () => navigate(location.state?.from ?? '/dashboard', { replace: true });

  const formik = useFormik({
    initialValues: { email: '', password: '', rememberMe: false },
    validationSchema: signInSchema,
    onSubmit: async (values, { setSubmitting }) => {
      try {
        // The token is stored by the mutation itself (see authApi's
        // onQueryStarted), so there's nothing to dispatch here.
        const data = await login({
          email: values.email.trim(),
          password: values.password,
          // Not part of the request — it decides whether the session renews
          // itself once the access token expires (see authApi).
          rememberMe: values.rememberMe,
        }).unwrap();

        // Password accepted, but the account has 2FA on: no session yet, just a
        // token to spend on the code. Stop here and let the dialog finish it.
        if (data?.requires_2fa) {
          setTempToken(data.temp_token);
          return;
        }

        goOn();
      } catch {
        // Already surfaced through `serverError` below — swallowing it here just
        // stops `unwrap()`'s rejection becoming an unhandled promise.
      } finally {
        setSubmitting(false);
      }
    },
  });

  /** Only show a field error once the user has left the field or tried to submit. */
  const fieldError = (name) => (formik.touched[name] ? t(formik.errors[name]) : undefined);

  /**
   * A full-page hand-off to Google rather than a popup, so it's `location.href`
   * and not `navigate`. Whatever the user was trying to reach travels with it
   * and comes back through /auth/google.
   */
  const signInWithGoogle = () => {
    // PrivateRoute puts the attempted path in `state`; `?next=` covers a link
    // built outside the app, which is how the old app passed it.
    const next = location.state?.from ?? new URLSearchParams(location.search).get('next');
    // The checkbox is on this form, and the session it applies to is created
    // when Google sends the trader back — so it has to be stashed, not passed.
    rememberForGoogle(formik.values.rememberMe);
    window.location.href = googleAuthUrl(next);
  };

  return (
    <div className="signin-page-split">
      {/* Left Slider Side */}
      <div className="signin-banner-side">
        <AuthSlider />
      </div>

      {/* Right Form Side */}
      <div className="signin-form-side">
        {/* Brand Logo - Top Left of the form side */}
        <div className="signin-brand-logo">
          <img
            src="/images/Logo.png"
            alt={t('Alpha Capital Logo')}
            className="logo-img"
          />
        </div>

        <div className="signin-form-content">
          {/* Header */}
          <div className="signin-form-header">
            <h1 className="signin-title">{t('Sign In')}</h1>
            <p className="signin-description">
              {t('Welcome Back! Please enter your details.')}
            </p>
          </div>

          {/* Form */}
          <form className="signin-form-body" onSubmit={formik.handleSubmit} noValidate>
            {/* Whatever the API rejected the whole attempt for — wrong password,
                locked account, network down. Field-level problems never reach here. */}
            {serverError && (
              <div className="signin-form-error" role="alert">
                {t(authErrorMessage(serverError))}
              </div>
            )}

            <Input
              label={t('Email')}
              type="email"
              name="email"
              placeholder={t('Enter your email')}
              value={formik.values.email}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={fieldError('email')}
              disabled={isLoading}
              autoComplete="email"
            />

            <Input
              label={t('Password')}
              type="password"
              name="password"
              placeholder={t('Enter your password')}
              value={formik.values.password}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={fieldError('password')}
              disabled={isLoading}
              autoComplete="current-password"
            />

            {/* Checkbox and Forgot Link */}
            <div className="signin-form-options">
              <label className="signin-remember-me">
                <input
                  type="checkbox"
                  name="rememberMe"
                  checked={formik.values.rememberMe}
                  onChange={formik.handleChange}
                  className="signin-checkbox"
                />
                <span className="signin-checkbox-label">
                  {t('Remember for 30 days')}
                </span>
              </label>
              <Link to="/forgot-password" className="signin-forgot-link">
                {t('Forgot password')}
              </Link>
            </div>

            {/* Submit Button */}
            <button type="submit" className="signin-submit-button" disabled={isLoading}>
              {isLoading ? t('Signing in…') : t('Sign In')}
            </button>

            <div className="signin-bottom-section">
              {/* OR Separator */}
              <div className="signin-divider">
                <div className="signin-divider-line" />
                <span className="signin-divider-text">{t('or')}</span>
                <div className="signin-divider-line" />
              </div>

              {/* Google Sign In Button */}
              <button
                type="button"
                className="signin-google-button"
                onClick={signInWithGoogle}
                disabled={isLoading}
              >
                <img src="/icons/Google.svg" alt="Google" style={{ width: '19px', height: '19px' }} />
                <span className="button-text">{t('Sign in with Google')}</span>
              </button>
              {/* Footer Navigation */}
              <div className="signin-footer-text">
                {t("Don't have an account?")}{" "}
                <Link to="/sign-up" className="signin-signup-link">
                  {t('Sign up')}
                </Link>
              </div>
            </div>
          </form>
        </div>

        {/* Copyright and legal links. The year is read, not written — a fixed
            one is wrong every January. */}
        <footer className="signin-copyright">
          <span>© {new Date().getFullYear()} Alpha Capital Group. {t('All rights reserved.')}</span>
          <nav className="signin-legal" aria-label={t('Legal')}>
            {LEGAL_LINKS.map(({ label, href }) => (
              <a key={label} href={href} target="_blank" rel="noreferrer">
                {t(label)}
              </a>
            ))}
          </nav>
        </footer>

        <TwoFactorDialog
          open={Boolean(tempToken)}
          tempToken={tempToken}
          // The form is still behind the dialog, ticked exactly as it was left.
          remember={formik.values.rememberMe}
          onCancel={() => setTempToken(null)}
          onVerified={() => {
            setTempToken(null);
            goOn();
          }}
        />
      </div>
    </div>
  );
}
